import { ancestorsSearch, getAncestorsExportUrl } from "@api/generated/ancestors/ancestors";
import type { AncestorHit, AncestorIn, Line } from "@api/generated/model";
import { apiDownloadJson } from "@api/http";
import { ONCE } from "@app/queryClient";
import { useSeasonFromLink } from "@app/workspace";
import { LineLink, LinePicker, lineColumn } from "@features/genealogy/GenotypeParts";
import { keyParam, TREE_DEPTH, TREE_MAX_DEPTH } from "@features/genealogy/genotype";
import { LineListCard, lineListBody, lineListEmpty, pickedSchema } from "@features/genealogy/LineList";
import { combineSchema, TermChips, termSchema } from "@features/genealogy/TermChips";
import { SavedQuerySelect } from "@features/queries/SavedQuerySelect";
import "@features/genealogy/genealogy.css";
import { Button, Loader, NumberInput, SegmentedControl } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatNumber, num } from "@ui/format";
import { notifyError } from "@ui/notify";
import { PageHeader } from "@ui/PageHeader";
import { RemovableChip } from "@ui/RemovableChip";
import { MissingList } from "@ui/RunBar";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const MODES = ["line", "genes", "query"] as const;
type Mode = (typeof MODES)[number];
/** A hit with the line whose tree it is in. */
type Found = AncestorHit & { line: Line; id: string };

export const Route = createFileRoute("/genealogy/ancestors")({
  validateSearch: z.object({
    ev: z.number().int().optional(),
    trials: z.array(z.string()).optional(), // every line of these trials
    q: z.number().int().optional(), // and of this saved query
    lines: z.array(pickedSchema).optional(), // and these
    mode: z.enum(MODES).optional(),
    target: pickedSchema.optional(),
    t: z.array(termSchema).optional(),
    c: combineSchema.optional(),
    cond: z.number().int().optional(), // mode "query": an ancestor among this saved query's lines
    depth: z.number().int().min(1).max(TREE_MAX_DEPTH).optional().catch(undefined),
  }),
  component: AncestorsPage,
});

/** Genealógia → Felmenők keresése (the legacy GenAnal "Find Parents having specified pattern/trait"): a genotype list
 *  (trials, a saved query, picked lines) searched for ancestors that are a given line, carry gene terms, or are lines
 *  of a saved query's register / phenotype conditions (and the terms), down to a depth; each hit with its generation,
 *  share, side and the path it came down. The search lives in the link (it survives a reload). */
function AncestorsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const season = useSeasonFromLink(search.ev);
  const [asked, setAsked] = useState<AncestorIn | null>(null);
  const mode: Mode = search.mode ?? "line";
  const terms = search.t ?? [];
  const depth = search.depth ?? TREE_DEPTH;
  const set = (patch: Partial<typeof search>) => void navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  const key = (p: { paz: number; sid: number }) => ({ paz: p.paz, sid: p.sid });
  const listed = !lineListEmpty(search);
  const pattern = mode === "line" ? !!search.target : mode === "genes" ? terms.length > 0 : search.cond != null;
  const body: AncestorIn | null =
    season && listed && pattern
      ? {
          ...lineListBody(season, search),
          ev: season,
          target: mode === "line" && search.target ? key(search.target) : null,
          terms: mode === "line" ? [] : terms,
          combine: search.c ?? "all",
          conditions: mode === "query" ? (search.cond ?? null) : null,
          depth,
        }
      : null;
  const result = useQuery({
    queryKey: ["ancestors", asked],
    queryFn: ({ signal }) => ancestorsSearch(asked as AncestorIn, { signal }),
    enabled: !!asked,
    ...ONCE,
  });
  useEffect(() => {
    if (result.error) notifyError(result.error);
  }, [result.error]);
  const data = asked ? result.data : undefined;
  // one row per hit, each naming its line (the hits of a line can be sorted apart)
  const hits = useMemo(
    () => data?.rows.flatMap((r) => r.hits.map((h, i): Found => ({ ...h, line: r.line, id: `${keyParam(r.line.key)}-${i}` }))),
    [data],
  );
  const columns = useMemo<Column<Found>[]>(
    () => [
      lineColumn("line", t("ancestors.col.line"), (h) => h.line),
      {
        id: "ancestor",
        header: t("ancestors.col.ancestor"),
        value: (h) => h.name,
        cell: (h) => <LineLink line={h.key ? { key: h.key, name: h.name } : null} text={h.name} />,
      },
      { id: "generation", header: t("ancestors.col.generation"), value: (h) => h.generation, numeric: true },
      {
        id: "share",
        header: t("ancestors.col.share"),
        value: (h) => h.share,
        numeric: true,
        text: (h) => `${formatNumber(h.share, 2)} %`,
      },
      { id: "branch", header: t("ancestors.col.branch"), value: (h) => t(`ancestors.branch.${h.side}`) },
      { id: "path", header: t("ancestors.col.path"), value: (h) => h.path.join(" ← "), className: "sm mu" },
    ],
    [t],
  );
  // the server stops at its limit of hits (``truncated``): the sort and the filters apply to those
  const table = useDataTable("genealogy.ancestors.hits", columns, hits, { partial: !!data?.truncated });
  const chips = <TermChips terms={terms} onChange={(next) => set({ t: next })} combine={search.c} onCombine={(c) => set({ c })} />;
  return (
    <div className="page fill">
      <PageHeader title={t("ancestors.title")} subtitle={t("ancestors.subtitle")} season />
      <div className="hstack" style={{ gap: 14, alignItems: "stretch", flexWrap: "wrap" }}>
        <LineListCard season={season} value={search} onChange={set} />
        <section className="card pad stack grow" style={{ gap: 8, minWidth: 320, flexBasis: 0 }}>
          <span className="sec">{t("ancestors.target")}</span>
          <SegmentedControl
            size="xs"
            value={mode}
            onChange={(v) => set({ mode: v as Mode })}
            data={MODES.map((m) => ({ value: m, label: t(`ancestors.mode.${m}`) }))}
          />
          {mode === "line" && (
            <>
              <LinePicker label={t("ancestors.line")} onPick={(k, name) => set({ target: { ...k, name } })} />
              {search.target && (
                <div className="chips">
                  <RemovableChip
                    tone="g"
                    removeLabel={t("ancestors.remove", { name: search.target.name })}
                    onRemove={() => set({ target: undefined })}
                  >
                    {search.target.name}
                  </RemovableChip>
                </div>
              )}
            </>
          )}
          {mode === "genes" && chips}
          {mode === "query" && (
            <>
              <SavedQuerySelect
                w={280}
                required
                label={t("ancestors.conditionQuery")}
                description={t("ancestors.conditionHelp")}
                value={search.cond ?? null}
                onChange={(id) => set({ cond: id ?? undefined })}
              />
              <span className="sm mu">{t("ancestors.andGenes")}</span>
              {chips}
            </>
          )}
          <div className="hstack" style={{ gap: 8, alignItems: "flex-end" }}>
            <NumberInput
              size="xs"
              w={110}
              label={t("ancestors.generations")}
              min={1}
              max={TREE_MAX_DEPTH}
              value={depth}
              onChange={(v) => set({ depth: num(v) ?? undefined })}
            />
            <MissingList
              requirements={[
                { label: t("ancestors.needList"), done: listed },
                { label: t(`ancestors.need.${mode}`), done: pattern },
              ]}
            />
            <Button size="xs" disabled={!body} loading={result.isFetching} onClick={() => setAsked(body)}>
              {t("ancestors.search")}
            </Button>
          </div>
        </section>
      </div>
      {asked && result.isFetching && !data && <Loader size="sm" m="md" />}
      {data && asked && (
        <section className="card stack grow" style={{ minHeight: 0, gap: 0, marginTop: 14 }}>
          <div className="hstack panel-head" style={{ gap: 8 }}>
            <span className="sec grow">{t("ancestors.result", { count: data.rows.length, total: data.searched })}</span>
            {data.truncated && <span className="chip y">{t("ancestors.truncated")}</span>}
            <Button
              size="compact-xs"
              variant="default"
              disabled={!data.rows.length}
              onClick={() => apiDownloadJson(getAncestorsExportUrl(), asked, t("ancestors.fileName")).catch(notifyError)}
            >
              {t("ancestors.download")}
            </Button>
          </div>
          <DataTable table={table} rowKey={(h) => h.id} className="grow" empty={t("ancestors.empty")} />
        </section>
      )}
    </div>
  );
}
